import React from 'react';
import { useTranslation } from 'react-i18next';
import { buttonClass, ghostButtonClass, inputClass } from '../../pages/picture-ai/usePictureAi';

/**
 * The compliance grid, edited by hand.
 *
 * A criterion's key is what makes two analyses comparable over time, so it is shown, never edited:
 * renaming a label keeps the key, and the history stays readable.
 */
const CriteriaGrid = ({ value = [], onChange, disabled, onSuggest, suggesting }) => {
  const { t } = useTranslation();

  const update = (index, patch) => onChange(value.map((criterion, position) => position === index ? { ...criterion, ...patch } : criterion));
  const remove = (index) => onChange(value.filter((_, position) => position !== index));
  const add = () => onChange([...value, { label: '', description: '', blocking: true }]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t('The model grades these criteria and nothing else. One unmet blocking criterion makes the picture non-compliant.')}
        </p>
        {onSuggest && (
          <button type="button" className={ghostButtonClass} onClick={onSuggest} disabled={disabled || suggesting}>
            {suggesting ? t('Drafting…') : t('Draft criteria from the description')}
          </button>
        )}
      </div>

      {value.length === 0 && (
        <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          {t('At least one criterion is required.')}
        </p>
      )}

      {value.map((criterion, index) => (
        <div key={criterion.key || `new-${index}`} className="rounded-xl border border-gray-200 p-3 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="flex-1 space-y-2">
              <input
                className={inputClass} value={criterion.label} disabled={disabled}
                placeholder={t('Criterion label')}
                onChange={event => update(index, { label: event.target.value })}
              />
              <input
                className={`${inputClass} text-xs`} value={criterion.description || ''} disabled={disabled}
                placeholder={t('What makes it met, observed on the picture')}
                onChange={event => update(index, { description: event.target.value })}
              />
            </div>
            <div className="flex w-40 shrink-0 flex-col items-start gap-2">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox" checked={criterion.blocking !== false} disabled={disabled}
                  onChange={event => update(index, { blocking: event.target.checked })}
                />
                {t('Blocking')}
              </label>
              {criterion.key && <code className="text-[11px] text-gray-400">{criterion.key}</code>}
              <button type="button" className="text-xs text-red-600 hover:underline disabled:opacity-50" disabled={disabled} onClick={() => remove(index)}>
                {t('Remove')}
              </button>
            </div>
          </div>
        </div>
      ))}

      <button type="button" className={buttonClass} disabled={disabled} onClick={add}>{t('Add a criterion')}</button>
    </div>
  );
};

export default CriteriaGrid;
